import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/routes/__root'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react'
import { format } from 'date-fns'

export const Route = createFileRoute('/admin/approve-users/')({
  beforeLoad: async () => {
    const user = queryClient.getQueryData(['user']) as any
    if (!user) {
      try {
        await queryClient.ensureQueryData({ queryKey: ['user'], queryFn: api.auth.getCurrentUser })
      } catch (error) {
        throw redirect({ to: '/auth/login', search: { redirect: '/admin/approve-users' } })
      }
    }

    // Check if user is admin
    const currentUser = queryClient.getQueryData(['user']) as any
    if (!currentUser || currentUser.email !== 'mandrew0987@gmail.com') {
      throw redirect({ to: '/' })
    }
  },
  loader: () =>
    queryClient.ensureQueryData({
      queryKey: ['admin', 'pendingUsers'],
      queryFn: api.admin.getPendingUsers,
    }),
  component: AdminApproveUsersPage,
})

function AdminApproveUsersPage() {
  const queryClient = useQueryClient()

  const { data: pendingUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'pendingUsers'],
    queryFn: api.admin.getPendingUsers,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const approveMutation = useMutation({
    mutationFn: ({ userId, action }: { userId: number; action: 'approve' | 'deny' }) =>
      api.admin.handleUserApproval(userId, action),
    onSuccess: (data) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['admin', 'pendingUsers'] })
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to process approval')
    },
  })

  const handleApproval = (userId: number, action: 'approve' | 'deny') => {
    approveMutation.mutate({ userId, action })
  }

  if (usersLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  const pendingUsersList = pendingUsers?.pending_users || []

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-primary">User Approvals</h1>
        <p className="text-muted-foreground">
          Manage user registration requests. Approve or deny pending account applications.
        </p>
      </div>

      {pendingUsersList.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">No Pending Users</h3>
            <p className="text-sm text-muted-foreground text-center">
              All user registrations have been processed. New requests will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {pendingUsersList.length} pending user{pendingUsersList.length !== 1 ? 's' : ''}
            </p>
            <Badge variant="secondary" className="text-xs">
              Auto-refreshing
            </Badge>
          </div>

          {pendingUsersList.map((user) => (
            <Card key={user.id} className="transition-all hover:shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{user.email}</CardTitle>
                    <CardDescription>
                      Registered {format(new Date(user.created_at), 'MMM d, yyyy at h:mm a')}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                    Pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApproval(user.id, 'approve')}
                    disabled={approveMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleApproval(user.id, 'deny')}
                    disabled={approveMutation.isPending}
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Deny
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
